import React, { useState, useEffect, useRef } from "react";
import { playClickSound } from "../utils/soundUtils.js";
import TextReveal from "./TextReveal.js";

function ConversationComponent({ conversation, onClose }) {
  const [currentPath, setCurrentPath] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showButtons, setShowButtons] = useState(!conversation.intro);
  const [visibleButtons, setVisibleButtons] = useState(0); // Track how many buttons should be visible
  const [showPersonResponse, setShowPersonResponse] = useState(false); // Track when to show person's response
  const firstButtonRef = useRef(null);
  const chosenButtonRef = useRef(null);

  // Reset state when conversation progresses
  useEffect(() => {
    setShowButtons(false);
    setVisibleButtons(0);
    setShowPersonResponse(false);
  }, [currentPath]);

  // Show buttons immediately for first conversation (no intro text to wait for)
  useEffect(() => {
    if (currentPath.length === 0 && !conversation.intro) {
      setShowButtons(true);
      startStaggeredFadeIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, conversation.intro]);

  // Handle intro fade-in completion
  useEffect(() => {
    if (currentPath.length === 0 && conversation.intro) {
      // Wait for fade-in animation (1 second) before showing buttons
      const timer = setTimeout(() => {
        setShowButtons(true);
        startStaggeredFadeIn();
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, conversation.intro]);

  // Handle when person's text finishes typing
  const handlePersonTextComplete = () => {
    setShowButtons(true);
    startStaggeredFadeIn();
  };

  // Handle when user's text finishes typing
  const handleUserTextComplete = () => {
    // Add 0.5 second pause before showing person's response
    setTimeout(() => {
      setShowPersonResponse(true);
    }, 500);
  };

  // Start staggered fade-in animation for buttons
  const startStaggeredFadeIn = () => {
    const currentNode = getCurrentNode();
    const totalButtons =
      currentPath.length === 0
        ? conversation.options.length + 1 // +1 for Leave Conversation button
        : currentNode.options
        ? currentNode.options.length + 1
        : 1; // +1 for Leave Conversation button

    // Reset visible buttons
    setVisibleButtons(0);

    // Fade in each button with a delay
    for (let i = 0; i < totalButtons; i++) {
      setTimeout(() => {
        setVisibleButtons(i + 1);
      }, i * 200); // 200ms delay between each button
    }
  };

  // Get current conversation node based on path
  const getCurrentNode = () => {
    // Start with the root conversation
    let current = conversation;

    // Navigate through the path
    for (let i = 0; i < currentPath.length; i++) {
      const pathIndex = currentPath[i];
      if (current.options && current.options[pathIndex]) {
        current = current.options[pathIndex];
      }
    }
    return current;
  };

  // Handle user choice selection
  const handleChoice = (choiceIndex) => {
    playClickSound();

    // Simply proceed to the next conversation level
    const newPath = [...currentPath, choiceIndex];
    setCurrentPath(newPath);
    setShowButtons(false);
    setVisibleButtons(0);
    setShowPersonResponse(false);
  };

  return (
    <div className="page-container">
      <div className="location-screen">
        <div className="location-header">
          <h2>{conversation.name}</h2>
        </div>

        <div className="conversation-description">
          {currentPath.length === 0 && conversation.intro && (
            <div className="intro-fade-in">{conversation.intro}</div>
          )}
          {currentPath.length > 0 && (
            <>
              {/* Show user's response above person's response */}
              {(() => {
                const previousNode = getCurrentNode();
                const previousChoiceIndex = currentPath[currentPath.length - 1];
                const previousChoice =
                  currentPath.length === 1
                    ? conversation.options[previousChoiceIndex]
                    : (() => {
                        let current = conversation;
                        for (let i = 0; i < currentPath.length - 1; i++) {
                          current = current.options[currentPath[i]];
                        }
                        return current.options[previousChoiceIndex];
                      })();

                return (
                  previousChoice && (
                    <div className="user-response">
                      <TextReveal
                        text={previousChoice.textLong || previousChoice.text}
                        speed={10}
                        onComplete={handleUserTextComplete}
                      />
                    </div>
                  )
                );
              })()}

              {/* Person's response */}
              {showPersonResponse && (
                <TextReveal
                  text={getCurrentNode().response}
                  speed={10}
                  onComplete={handlePersonTextComplete}
                />
              )}
            </>
          )}
        </div>

        <div className="options-grid">
          {currentPath.length === 0
            ? // Show initial user options
              conversation.options.map((option, index) => {
                return (
                  <div key={index} className="option-row">
                    <button
                      ref={index === 0 ? firstButtonRef : null}
                      className={`conversation-button ${
                        index < visibleButtons ? "fade-in" : ""
                      }`}
                      onClick={() => handleChoice(index)}
                    >
                      {option.text}
                    </button>
                  </div>
                );
              })
            : // Show options from the current node
              getCurrentNode().options &&
              getCurrentNode().options.map((option, index) => {
                return (
                  <div key={index} className="option-row">
                    <button
                      ref={index === 0 ? firstButtonRef : null}
                      className={`conversation-button ${
                        index < visibleButtons ? "fade-in" : ""
                      }`}
                      onClick={() => handleChoice(index)}
                    >
                      {option.text}
                    </button>
                  </div>
                );
              })}

          <div className="option-row">
            <button
              className={`leave-conversation-button ${
                (currentPath.length === 0
                  ? conversation.options.length
                  : getCurrentNode().options
                  ? getCurrentNode().options.length
                  : 0) < visibleButtons
                  ? "fade-in"
                  : ""
              }`}
              onClick={() => {
                const currentNode = getCurrentNode();
                if (currentNode.options && currentNode.options.length > 0) {
                  // There are more options, just close
                  onClose();
                } else {
                  // Check if parent has loopBack
                  const parentPath = currentPath.slice(0, -1);
                  const parentNode =
                    parentPath.length > 0
                      ? (() => {
                          let current = conversation;
                          for (let i = 0; i < parentPath.length; i++) {
                            current = current.options[parentPath[i]];
                          }
                          return current;
                        })()
                      : conversation;

                  if (parentNode.loopBack) {
                    // Go back to parent level
                    setCurrentPath(parentPath);
                  } else {
                    // No more options, go back to first level
                    setCurrentPath([]);
                  }
                }
              }}
            >
              {(() => {
                const currentNode = getCurrentNode();
                if (currentNode.options && currentNode.options.length > 0) {
                  return "Leave Conversation";
                } else {
                  // Check if parent has loopBack
                  const parentPath = currentPath.slice(0, -1);
                  const parentNode =
                    parentPath.length > 0
                      ? (() => {
                          let current = conversation;
                          for (let i = 0; i < parentPath.length; i++) {
                            current = current.options[parentPath[i]];
                          }
                          return current;
                        })()
                      : conversation;

                  if (parentNode.loopBack) {
                    return "OK";
                  } else {
                    return "Continue";
                  }
                }
              })()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConversationComponent;
